<template>
  <div class="audio-recorder text-xs-center">
    <audio
      v-if="audio !== null"
      :src="audio"
      controls
      controlsList="nodownload"
    />
    <div
      v-if="isRecordingEnabled"
      class="text-xs-center"
    >
      <v-layout
        align-center
        :justify-center="audio === null"
        :justify-space-between="audio !== null"
      >
        <v-btn
          small
          outline
          class='branding-active-text branding-active-border'
          @click="recordAudio"
          v-show="!isRecording"
          :disabled="isProcessingData"
        >
          <v-icon
            small
            class="mr-2"
          >
            fiber_manual_record
          </v-icon>
          Record
        </v-btn>
        <v-btn
          small
          outline
          :color="(isRecording) ? 'error' : 'primary'"
          @click="stopAudio"
          v-show="isRecording"
        >
          <v-icon
            small
            class="mr-2"
          >
            stop
          </v-icon>
          {{ recordingDuration || '0:00' }}
        </v-btn>
        <v-btn
          small
          outline
          class="branding-active-text branding-active-border"
          @click="uploadAudio"
          v-show="audio !== null"
          :loading="isProcessingData"
          :disabled="isUploadAudioDisabled"
        >
          <v-icon
            small
            class="mr-2"
          >
            file_upload
          </v-icon>
          Upload
        </v-btn>
        <v-btn
          small
          outline
          color="error"
          @click="resetRecording({
            resetRecorder: true,
            exitRecordAudioMode: true,
          })"
          v-show="audio !== null"
          :disabled="isProcessingData"
        >
          <v-icon
            small
            class="mr-2"
          >
            clear
          </v-icon>
          Cancel
        </v-btn>
      </v-layout>
      <div class="mt-2 grey--text text--darken-2 caption">
        Recordings are limited to 90 seconds.
      </div>
    </div>
    <div v-else>
      <v-alert
        color="warning"
        icon="priority_high"
        value="true"
      >
        Unable to record audio.
        <a
          href="https://knowledge.playposit.com/article/189-audio-recording"
          class="white--text underline"
          target="_blank"
        >
          Click here to learn how to enable audio recording in your browser.
        </a>
      </v-alert>
    </div>
  </div>
</template>

<script>
import RecordRTC from 'recordrtc';

export default {
  name: 'DiscussionAudioRecording',
  props: {
    isProcessingData: Boolean,
  },
  data: () => ({
    audio: null,
    mediaStream: null,
    isRecording: false,
    recordingTime: {
      start: 0,
      current: 0,
    },
    recordingTimeInterval: false,
    recorder: {},
    recordedBlob: null,
    isRecordingEnabled: true,
  }),
  computed: {
    isUploadAudioDisabled() {
      return (
        this.isProcessingData
        || this.audio === null
        || this.recordedBlob === null
        || this.isRecording
      );
    },
    recordingDuration() {
      if (
        this.recordingTime.start === 0
        || this.recordingTime.current === 0
      ) {
        return new Date(0).toISOString().substr(14, 5);
      }
      return new Date(Math.round(((this.recordingTime.current - this.recordingTime.start)) / 1000) * 1000).toISOString().substr(14, 5);
    },
  },
  methods: {
    resetRecording({
      resetRecorder = false,
      exitRecordAudioMode = false,
    } = {}) {
      if (resetRecorder) this.recorder.reset();
      this.audio = null;
      this.recordedBlob = null;
      this.isRecording = false;
      this.recordingTime.start = 0;
      this.recordingTime.current = 0;
      if (exitRecordAudioMode) this.$emit('exit-record-audio-mode');
    },
    recordAudio() {
      this.resetRecording({ resetRecorder: true });
      this.isRecording = true;
      this.recordingTime.start = Date.now();
      this.recorder.startRecording();
    },
    stopAudio() {
      this.resetRecording();
      this.recorder.stopRecording((audioVideoWebMURL) => {
        this.audio = audioVideoWebMURL;
        this.recordedBlob = this.recorder.getBlob();
        this.stopMediaStream();
      });
    },
    stopMediaStream() {
      if (
        this.mediaStream
        && this.$_.isFunction(this.mediaStream.getTracks)
      ) {
        for (const track of this.mediaStream.getTracks()) {
          track.stop();
        }
      }
      this.mediaStream = null;
    },
    toggleRecordingTimeInterval() {
      if (!this.recordingTimeInterval) {
        this.recordingTimeInterval = setInterval(() => {
          this.recordingTime.current = Date.now();
        }, 1000);
      } else {
        clearInterval(this.recordingTimeInterval);
        this.recordingTimeInterval = false;
      }
    },
    async initializeAudioRecording() {
      try {
        if (
          !navigator.getUserMedia
          && navigator.mediaDevices.getUserMedia
        ) {
          this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } else {
          this.mediaStream = await new Promise((resolve, reject) => navigator.getUserMedia({ audio: true }, resolve, reject));
        }
        this.isRecordingEnabled = true;
        this.recorder = RecordRTC(this.mediaStream, {
          type: 'audio',
          recorderType: RecordRTC.StereoAudioRecorder,
        });
      } catch(e) {
        this.isRecordingEnabled = false;
        this.$emit('error', e);
      }
    },
    uploadAudio() {
      if (!this.recordedBlob || this.audioDuration > 90) { // Roughly 90 seconds of audio
        return this.$emit('error', new Error('Audio duration must be 90 seconds or less.'));
      }
      return this.$emit('upload-audio', this.recordedBlob);
    },
  },
  watch: {
    isRecording() {
      this.toggleRecordingTimeInterval();
    },
  },
  beforeDestroy() {
    this.stopMediaStream();
  },
  async mounted() {
    await this.initializeAudioRecording();
  },
};
</script>